import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { addMeal, getMeals, updateMeal } from "../api/meals";
import { AuthContext } from "../context/AuthContext";
import { importRecipeFromUrl } from "../utils/recipeImport";

const SECTIONS = [
  "Produce",
  "Dairy & Eggs",
  "Meat & Seafood",
  "Bakery",
  "Pantry",
  "Frozen",
  "Beverages",
  "Other"
];

const EMPTY_INGREDIENT = { name:"", quantity:"", unit:"", section:"Other" };

export default function MealFormPage({ meals, setMeals }){

  const { id } = useParams();
  const navigate = useNavigate();
  const { householdId } = useContext(AuthContext);
  const isEditing = !!id;

  const [name, setName] = useState("");
  const [servings, setServings] = useState("");
  const [ingredients, setIngredients] = useState([{ ...EMPTY_INGREDIENT }]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const [importUrl, setImportUrl]       = useState("");
  const [importing, setImporting]       = useState(false);
  const [importError, setImportError]   = useState("");
  const [importSource, setImportSource] = useState("");

  useEffect(()=>{

    if(!isEditing) return;

    const existing = meals.find(m=>m.id===id);
    if(!existing) return;

    setName(existing.name || "");
    setServings(existing.servings || "");
    setTags(existing.tags || []);
    setNotes(existing.notes || "");

    const ings = (existing.ingredients || []).map(ing=>
      typeof ing === "string"
        ? { name:ing, quantity:"", unit:"", section:"Other" }
        : { section:"Other", ...ing }
    );

    setIngredients(ings.length ? ings : [{ ...EMPTY_INGREDIENT }]);

  },[id, meals, isEditing]);

  async function handleImport(){
    const url = importUrl.trim();
    if(!url) return;
    setImporting(true);
    setImportError("");
    setImportSource("");
    try{
      const result = await importRecipeFromUrl(url);
      setName(result.name);
      setServings(result.servings);
      setIngredients(result.ingredients);
      setNotes(result.notes);
      setImportSource(result.sourceName);
      setImportUrl("");
    } catch(err){
      setImportError(err.message);
    } finally {
      setImporting(false);
    }
  }

  function handleIngredientChange(index, field, value){
    setIngredients(prev=>
      prev.map((ing,i)=> i===index ? { ...ing, [field]:value } : ing)
    );
  }

  function handleTagKeyDown(e){
    if(e.key !== "Enter" && e.key !== ",") return;
    e.preventDefault();
    const trimmed = tagInput.trim().toLowerCase();
    if(trimmed && !tags.includes(trimmed)){
      setTags(prev=>[...prev, trimmed]);
    }
    setTagInput("");
  }

  function removeTag(tag){
    setTags(prev=>prev.filter(t=>t!==tag));
  }

  function addIngredient(){
    setIngredients(prev=>[...prev, { ...EMPTY_INGREDIENT }]);
  }

  function removeIngredient(index){
    setIngredients(prev=>prev.filter((_,i)=>i!==index));
  }

  async function handleSave(){
    setSaving(true);
    try{
      const meal = { name, servings, ingredients, tags, notes };
      if(isEditing){
        await updateMeal(id, meal);
      } else {
        await addMeal(meal, householdId);
      }
      const data = await getMeals(householdId);
      setMeals(data);
      navigate("/meals");
    } finally {
      setSaving(false);
    }
  }

  return(

    <div className="card mealForm">

      <h2>{isEditing ? "Edit Meal" : "New Meal"}</h2>

      {/* IMPORT FROM URL */}
      <div className="importSection">
        <p className="importLabel">
          Import from a recipe website
          <span className="importHint"> — works with AllRecipes, BBC Good Food, Serious Eats, NYT Cooking, and more</span>
        </p>
        <div className="importRow">
          <input
            className="importInput"
            type="url"
            placeholder="Paste a recipe URL…"
            value={importUrl}
            onChange={e=>setImportUrl(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter") handleImport(); }}
            disabled={importing}
          />
          <button
            className="importBtn"
            onClick={handleImport}
            disabled={importing || !importUrl.trim()}
          >
            {importing ? "Importing…" : "Import"}
          </button>
        </div>
        {importError  && <p className="importError">{importError}</p>}
        {importSource && (
          <p className="importSuccess">
            ✓ Imported from {importSource}. Review the fields below and save when ready.
          </p>
        )}
      </div>

      <div className="formGroup">
        <label>Meal Name</label>
        <input
          placeholder="e.g. Spaghetti Bolognese"
          value={name}
          onChange={e=>setName(e.target.value)}
        />
      </div>

      <div className="formGroup">
        <label>Serves</label>
        <input
          type="number"
          inputMode="decimal"
          placeholder="Number of people"
          value={servings}
          onChange={e=>setServings(e.target.value)}
          style={{maxWidth:160}}
        />
      </div>

      <div className="formGroup">
        <label>Tags</label>
        <div className="tagInputArea">
          {tags.map(tag=>(
            <span key={tag} className="tagChip">
              {tag}
              <button className="tagRemove" onClick={()=>removeTag(tag)}>×</button>
            </span>
          ))}
          <input
            placeholder="Add a tag, press Enter"
            value={tagInput}
            onChange={e=>setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
          />
        </div>
      </div>

      <h3 style={{marginBottom:10}}>Ingredients</h3>

      {ingredients.map((ing,i)=>(
        <div key={i} className="ingredientRow">

          <input
            placeholder="Ingredient"
            value={ing.name}
            onChange={e=>handleIngredientChange(i,"name",e.target.value)}
          />

          <input
            placeholder="Qty"
            inputMode="decimal"
            value={ing.quantity}
            onChange={e=>handleIngredientChange(i,"quantity",e.target.value)}
          />

          <input
            placeholder="Unit"
            value={ing.unit}
            onChange={e=>handleIngredientChange(i,"unit",e.target.value)}
          />

          <select
            className="sectionSelect"
            value={ing.section || "Other"}
            onChange={e=>handleIngredientChange(i,"section",e.target.value)}
          >
            {SECTIONS.map(s=>(
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          {ingredients.length > 1 && (
            <button
              className="removeBtn"
              onClick={()=>removeIngredient(i)}
            >
              ×
            </button>
          )}

        </div>
      ))}

      <button
        className="addIngredientBtn"
        onClick={addIngredient}
      >
        + Add Ingredient
      </button>

      <div className="formGroup" style={{marginTop:8}}>
        <label>Notes &amp; Links</label>
        <textarea
          className="notesTextarea"
          placeholder="Add instructions, links, or any notes about this recipe..."
          value={notes}
          onChange={e=>setNotes(e.target.value)}
        />
      </div>

      <div className="formActions">
        <button onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Meal"}
        </button>
        <button
          className="discardBtn"
          onClick={()=>navigate("/meals")}
        >
          Discard
        </button>
      </div>

    </div>

  );

}
